import type { Dispatch } from 'redux'
import { Cart, Wishlist, ResponseError, nativeAppHeaders } from '@ynap/api-utils'
import { timeout, delay } from '@ynap/promise-utils'
import { notifyBasketChanged, getLocale } from '@ynap/cart-state'
import { getWatsonConfig, getTimeout, getDelay } from '@ynap/cart-state/src/selectors/getConfig'
import { OrderItem, ShoppingCart } from 'app/types/common-types'
import { StoreState } from 'app/state/types'
import {
    UNDO_MOVE_TO_WISHLIST_REQUEST,
    UNDO_MOVE_TO_WISHLIST_SUCCESS,
    UNDO_MOVE_SOLD_OUT_TO_WISHLIST_SUCCESS,
    UNDO_MOVE_TO_WISHLIST_ERROR,
} from '../constants/ActionTypes'
import { getAdditionalCheckoutHeaders } from '../utils/checkoutActionUtils'

interface State extends StoreState {
    cart: ShoppingCart & {
        orderItemIncludingRemoved: OrderItem[]
    }
}

type UndoMoveItemToWishlistResponseData = {
    orderId: string
    orderItem: { orderItemId: number }[]
    shoppingCart: ShoppingCart
    version: string
    resourceId: string
}

export default function undoMoveToWishlist({
    partNumber,
    orderItemId,
    giftListItemID,
    soldOut,
    reservationId,
}: {
    partNumber: string
    orderItemId: number
    giftListItemID: string
    soldOut?: boolean
    reservationId?: string
}) {
    return (dispatch: Dispatch, getState: () => State, fetch: WindowOrWorkerGlobalScope['fetch']) => {
        const state = getState()
        const watsonConfig = getWatsonConfig(state)
        const clientTimeout = getTimeout(state)
        const { langId } = getLocale(state)
        const additionalHeaders = {
            ...nativeAppHeaders(state.deviceType),
            ...getAdditionalCheckoutHeaders(state),
        }
        dispatch(undoMoveToWishlistRequest(partNumber))

        const removeFromWishlist = () => {
            const wishlistRequest = Wishlist.removeItem('INVENTORY', '@default', giftListItemID, watsonConfig)
            return timeout(fetch(wishlistRequest), clientTimeout)
        }
        if (soldOut) {
            return removeFromWishlist()
                .catch((error: unknown) => dispatch(undoMoveToWishlistError(partNumber, error)))
                .then(() => dispatch(undoMoveSoldOutToWishlistSuccess(partNumber)))
        }

        const { orderItemIncludingRemoved } = state.cart // TODO: Should use shared getCart selector?
        const orderItemToUndo = orderItemIncludingRemoved.find((item) => item.partNumber === partNumber)
        const updateRequest = Cart.addToCart({
            partNumber,
            quantity: orderItemToUndo?.quantity,
            ...watsonConfig,
            includeShoppingCart: true,
            langId,
            reservationId,
            additionalHeaders,
        })
        return delay(
            timeout(fetch(updateRequest), clientTimeout)
                .then(async (updateResponse: Response) => {
                    if (!updateResponse.ok) {
                        throw new ResponseError(updateResponse)
                    }
                    const data: UndoMoveItemToWishlistResponseData = await updateResponse.json()

                    if (giftListItemID) {
                        return (
                            removeFromWishlist()
                                .catch()
                                // ignore wishlist api error - cart can still be updated
                                .then(() => {
                                    notifyBasketChanged()
                                    return dispatch(undoMoveToWishlistSuccess(partNumber, orderItemId, data))
                                })
                        )
                    }
                    notifyBasketChanged()
                    return dispatch(undoMoveToWishlistSuccess(partNumber, orderItemId, data))
                })
                .catch((error: unknown) => {
                    dispatch(undoMoveToWishlistError(partNumber, error))
                    throw error
                }),
            getDelay(state)
        )
    }
}

function undoMoveToWishlistRequest(partNumber: string) {
    return {
        type: UNDO_MOVE_TO_WISHLIST_REQUEST,
        partNumber,
    }
}

function undoMoveToWishlistSuccess(partNumber: string, orderItemId: number, payload: UndoMoveItemToWishlistResponseData) {
    return {
        type: UNDO_MOVE_TO_WISHLIST_SUCCESS,
        partNumber,
        orderItemId,
        payload,
    }
}

function undoMoveSoldOutToWishlistSuccess(partNumber: string) {
    return {
        type: UNDO_MOVE_SOLD_OUT_TO_WISHLIST_SUCCESS,
        partNumber,
    }
}

function undoMoveToWishlistError(partNumber: string, error: unknown) {
    return {
        type: UNDO_MOVE_TO_WISHLIST_ERROR,
        partNumber,
        error,
    }
}
